import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { PaymentMethodsManager } from '@ctct/application-layer';
import { BillingStrategy, type BillingStrategyQuery, type RatingCallRequest } from '@ctct/domain-layer';
import type { PaymentMethodsHooks, PaymentMethodsPageStore } from '@ctct/state-client-layer';
import type { PaymentStrategyQuery, StripeElementsCredentialsArgs } from '@ctct/state-server-layer';
import {
  PaymentStrategy,
  shouldFetchStripeCustomer,
  useBillingAccount,
  useBillingInfo,
  useCreateAmazonPaySession,
  useCreateAmazonPaySignature,
  useCreatePaypalAgreement,
  useCreatePaypalToken,
  useCustomerInfo,
  useEnableAmexIrAffiliate,
  useIsCanceled,
  useIsImpersonated,
  usePartnerName,
  useRating,
  useStripeCustomer,
  useStripeElementsCredentials,
  useUpdateSetupIntentParams,
} from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';
import { resolvePaymentStrategyQuery } from '../helpers/resolveStrategyQuery';

interface Props {
  initializer: Pick<PaymentMethodsManager, 'paymentMethodsInit'>;
  pageStore: PaymentMethodsPageStore;
  hooks: PaymentMethodsHooks;
  hideHeading?: boolean;
  isApplePayEnabled?: boolean;
  isNewPayflow?: boolean;
  isNewCustomerPayflow?: boolean;
  disableApplePayAsNewPaymentMethod?: boolean;
  billingStrategy: BillingStrategyQuery;
  paymentStrategy: PaymentStrategyQuery;
  externalHooks: {
    useRatingCallPayload: () => RatingCallRequest | null;
  };
  flags?: {
    /**
     * Ignore rating call when calculating pending state?
     *
     * This is useful for pages such as Overdue Balance where we don't make a rating call.
     */
    shouldIgnoreRatingCall?: boolean;
  };
}

type StripeElementsCredentialsArgsInput = Pick<Props, 'billingStrategy' | 'paymentStrategy'> & {
  quoteId: string | null;
  countryCode: string | null;
  currencyCode: string | null;
  paymentMethodId: string | null;
};

export const createStripeElementsCredentialsArgs = ({
  billingStrategy,
  paymentStrategy,
  quoteId,
  countryCode,
  currencyCode,
  paymentMethodId,
}: StripeElementsCredentialsArgsInput): StripeElementsCredentialsArgs => {
  const isStripePayment = paymentStrategy.data === PaymentStrategy.Stripe;

  if (billingStrategy.data === BillingStrategy.InHouse && isStripePayment) {
    return { useCase: 'payments-only' as const, countryCode, currencyCode, paymentMethodId, enabled: true };
  }

  return {
    useCase: 'billing' as const,
    quoteId,
    countryCode,
    currencyCode,
    paymentMethodId,
    enabled: isStripePayment,
  };
};

const resolveBillingStrategyEnum = (billingStrategy: BillingStrategyQuery): BillingStrategy =>
  billingStrategy.isPending ? BillingStrategy.InHouse : billingStrategy.data;

export const usePaymentMethodsWidget = ({
  initializer,
  pageStore,
  hooks,
  hideHeading,
  isApplePayEnabled = false,
  isNewPayflow = false,
  isNewCustomerPayflow = false,
  disableApplePayAsNewPaymentMethod = false,
  billingStrategy,
  paymentStrategy,
  externalHooks,
  flags,
}: Props) => {
  const { t } = useTranslation();
  const { data: isImpersonated = false } = useIsImpersonated();

  const billingInfoQuery = useBillingInfo();
  const billingAccountQuery = useBillingAccount();
  const partnerNameQuery = usePartnerName();
  const isAmexIrAffiliateQuery = useEnableAmexIrAffiliate();
  const customerInfoQuery = useCustomerInfo();

  const isCanceled = useIsCanceled();
  const isCustomer = Boolean(billingInfoQuery.data?.getIsCustomer() || isCanceled);
  const resolvedBillingStrategy = resolveBillingStrategyEnum(billingStrategy);
  const resolvedPaymentStrategy = resolvePaymentStrategyQuery(paymentStrategy);
  const stripeCustomerQuery = useStripeCustomer(
    shouldFetchStripeCustomer({
      isStripeBilling: billingStrategy.data === BillingStrategy.Stripe,
      isStripePayments: resolvedPaymentStrategy.data === PaymentStrategy.Stripe,
      isCustomer,
    })
  );

  const createPaypalTokenMutation = useCreatePaypalToken();
  const createPaypalAgreementMutation = useCreatePaypalAgreement();
  const createAmazonPaySignatureMutation = useCreateAmazonPaySignature();
  const createAmazonPaySessionMutation = useCreateAmazonPaySession();

  const ratingCallPayload = externalHooks.useRatingCallPayload();
  const ratingQuery = useRating(ratingCallPayload);

  const stripeQuoteId = ratingQuery.data?.getStripeQuoteId() ?? null;

  const currencyCode = ratingQuery.data?.getCurrency() ?? billingAccountQuery.data?.getCurrency() ?? null;

  const shouldIgnoreRatingCall = flags?.shouldIgnoreRatingCall ?? false;

  const countryCode = (billingInfoQuery.data?.getCountry()?.trim() || customerInfoQuery.data?.getCountryCode()) ?? null;

  const stripeCustomerPaymentMethodId = stripeCustomerQuery.data?.getPaymentMethod()?.getToken() ?? null;

  const stripeElementsCredentialsArgs = createStripeElementsCredentialsArgs({
    billingStrategy,
    paymentStrategy: resolvedPaymentStrategy,
    quoteId: stripeQuoteId,
    countryCode,
    currencyCode,
    paymentMethodId: stripeCustomerPaymentMethodId,
  });
  const stripeSetupCredentialsQuery = useStripeElementsCredentials(stripeElementsCredentialsArgs);

  const updateSetupIntentParamsMutation = useUpdateSetupIntentParams({
    useCase: stripeElementsCredentialsArgs.useCase,
    quoteId: stripeQuoteId,
    paymentMethodId: stripeCustomerPaymentMethodId,
  });

  const { lifecycle, ...componentProps } = useMemo(() => {
    const initedManagerReturnValue = initializer.paymentMethodsInit({
      pageStore,
      queries: [
        billingInfoQuery,
        billingAccountQuery,
        partnerNameQuery,
        isAmexIrAffiliateQuery,
        stripeSetupCredentialsQuery,
        ratingQuery,
        stripeCustomerQuery,
      ],
      mutations: [
        createPaypalTokenMutation,
        createPaypalAgreementMutation,
        createAmazonPaySignatureMutation,
        createAmazonPaySessionMutation,
        updateSetupIntentParamsMutation,
      ],
      isApplePayEnabled,
      isImpersonated,
      isNewPayflow,
      isNewCustomerPayflow,
      t,
      billingStrategy: resolvedBillingStrategy,
      paymentStrategy: resolvedPaymentStrategy,
      stripeLocaleCountryCode: countryCode,
      flags: {
        shouldIgnoreRatingCall,
      },
    });

    return {
      ...initedManagerReturnValue,
      hooks,
      hideHeading,
      isApplePayEnabled,
      disableApplePayAsNewPaymentMethod,
      externalHooks,
    };
  }, [
    initializer,
    pageStore,
    billingInfoQuery,
    billingAccountQuery,
    partnerNameQuery,
    isAmexIrAffiliateQuery,
    stripeSetupCredentialsQuery,
    ratingQuery,
    stripeCustomerQuery,
    createPaypalTokenMutation,
    createPaypalAgreementMutation,
    createAmazonPaySignatureMutation,
    createAmazonPaySessionMutation,
    updateSetupIntentParamsMutation,
    isApplePayEnabled,
    isImpersonated,
    isNewPayflow,
    isNewCustomerPayflow,
    t,
    resolvedBillingStrategy,
    resolvedPaymentStrategy,
    countryCode,
    hooks,
    hideHeading,
    disableApplePayAsNewPaymentMethod,
    externalHooks,
    shouldIgnoreRatingCall,
  ]);

  useWidgetLifecycle(componentProps.status, lifecycle);

  return componentProps;
};

export type PaymentMethodsWidgetProps = ReturnType<typeof usePaymentMethodsWidget>;

export type LoadedInHousePaymentMethodsWidgetProps = Extract<
  PaymentMethodsWidgetProps,
  {
    status: 'loaded';
    paymentStrategy: PaymentStrategy.Paas;
  }
>;

export type LoadedStripePaymentMethodsWidgetProps = Extract<
  PaymentMethodsWidgetProps,
  {
    status: 'loaded';
    paymentStrategy: PaymentStrategy.Stripe;
  }
>;
